// src/components/layout/MainMenuClient.tsx
"use client";

import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowIcon } from "../ui/Icons";
import { Button } from "@/components/ui/Button";
import AccountIconLinkClient from "@/components/layout/AccountIconLinkClient";
import CartBadgeClient from "@/components/layout/CartBadgeClient";
import { DEFAULT_LANGUAGE } from "@/lib/languages";

type MenuItemImage = {
  url: string;
  alt?: string;
  width?: number;
  height?: number;
};

type MegaImage = {
  id?: number;
  url: string;
  alt?: string;
  width?: number;
  height?: number;
  linkUrl?: string;
  cta?: string;
};

type MenuNode = {
  ID: number;
  title: string;
  url: string;
  target?: string | null;
  children: MenuNode[];
  mega?: {
    image_first?: MegaImage | null;
    image_second?: MegaImage | null;
  };
};

function isPlaceholder(url?: string | null) {
  const u = (url ?? "").trim();
  return !u || u === "#";
}

function isExternal(url?: string | null) {
  const u = (url ?? "").trim();
  return u.startsWith("http://") || u.startsWith("https://");
}

function Html({ html }: { html: string }) {
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

function MenuLabel({ html, active }: { html: string; active?: boolean }) {
  const isActive = !!active;

  return (
    <span className="inline-grid place-items-center text-body leading-none">
      <span
        className={[
          "col-start-1 row-start-1 transition-opacity duration-100 ease-out",
          isActive ? "opacity-0" : "opacity-100 group-hover:opacity-0",
        ].join(" ")}
      >
        <Html html={html} />
      </span>

      <span
        aria-hidden="true"
        className={[
          "col-start-1 row-start-1 font-medium transition-opacity duration-100 ease-out",
          isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100",
        ].join(" ")}
        style={{ pointerEvents: "none" }}
      >
        <Html html={html} />
      </span>

      <span aria-hidden="true" className="invisible font-bold col-start-1 row-start-1">
        <Html html={html} />
      </span>
    </span>
  );
}

function MegaImageBox({ image, href }: { image: MegaImage; href?: string }) {
  const ratioW = 3;
  const ratioH = 2;

  return (
    <div className="w-full">
      <div className="relative w-full overflow-hidden" style={{ aspectRatio: `${ratioW} / ${ratioH}` }}>
        <Image
          src={image.url}
          alt={image.alt || ""}
          fill
          className="object-cover"
          sizes="(min-width: 1024px) 25vw, 100vw"
        />

        {image.cta && href && (
          <div className="absolute left-3 bottom-3">
            <Button
              as="link"
              href={href}
              bgStyle="bg-white/80 hover:bg-white border-white hover:border-white shadow-flat"
              className="px-4"
              arrowStyle="text-fuji-black"
              icon={<ArrowIcon direction="right" />}
            >
              {image.cta}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function MenuItemImageBox({ img, fallbackAlt }: { img: MenuItemImage; fallbackAlt: string }) {
  const w = img.width ?? 140;
  const h = img.height ?? 40;

  return (
    <span className="relative block h-[18px]" style={{ aspectRatio: `${w} / ${h}` }}>
      <Image src={img.url} alt={img.alt || fallbackAlt} fill className="object-contain" sizes="140px" />
    </span>
  );
}

function HamburgerIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {open ? (
        <g>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </g>
      ) : (
        <g>
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </g>
      )}
    </svg>
  );
}

export default function MainMenuClient({
  topTree,
  activeLang,
  imageById,
  siteName,
  homeHref,
  logoSrc,
  showAccountIcon,
}: {
  topTree: MenuNode[];
  activeLang: string;
  imageById: Record<string, MenuItemImage>;
  siteName: string;
  homeHref: string;
  logoSrc: string;
  showAccountIcon?: boolean;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeTopId, setActiveTopId] = useState<number | null>(null);

  const isDefaultLang = activeLang === DEFAULT_LANGUAGE.polylangCode;

  // --- Sliding indicator bar (desktop mega menu)
  const navRef = useRef<HTMLUListElement | null>(null);
  const itemRefs = useRef<Record<number, HTMLLIElement | null>>({});
  const [indicator, setIndicator] = useState<{
    left: number;
    width: number;
    visible: boolean;
    noTransition: boolean;
  }>({
    left: 0,
    width: 0,
    visible: false,
    noTransition: true,
  });

  // --- indicator reset + grow behaviour
  const INDICATOR_MS = 200;
  const rafRef = useRef<number | null>(null);
  const hideTimerRef = useRef<number | null>(null);
  const lastIdRef = useRef<number | null>(null);

  const clearTimers = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;

    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = null;
  };

  const getTargetForId = (id: number | null) => {
    if (!id) return null;

    const li = itemRefs.current[id];
    const ul = navRef.current;

    if (!li || !ul) return null;

    const liRect = li.getBoundingClientRect();
    const ulRect = ul.getBoundingClientRect();

    return {
      left: liRect.left - ulRect.left,
      width: liRect.width,
    };
  };

  const collapseIndicator = () => {
    clearTimers();
    lastIdRef.current = null;

    setIndicator((p) => ({
      left: p.left,
      width: 0,
      visible: true,
      noTransition: false,
    }));

    hideTimerRef.current = window.setTimeout(() => {
      setIndicator({
        left: 0,
        width: 0,
        visible: false,
        noTransition: true,
      });
    }, INDICATOR_MS);
  };

  const showIndicatorForId = (id: number | null) => {
    const target = getTargetForId(id);

    if (!target) {
      collapseIndicator();
      return;
    }

    const isFirstEnter = !lastIdRef.current;
    lastIdRef.current = id;

    clearTimers();

    if (isFirstEnter) {
      setIndicator({
        left: target.left,
        width: 0,
        visible: true,
        noTransition: true,
      });

      rafRef.current = requestAnimationFrame(() => {
        setIndicator({
          left: target.left,
          width: target.width,
          visible: true,
          noTransition: false,
        });
      });

      return;
    }

    setIndicator({
      left: target.left,
      width: target.width,
      visible: true,
      noTransition: false,
    });
  };

  useLayoutEffect(() => {
    showIndicatorForId(activeTopId);

    const onResize = () => showIndicatorForId(activeTopId);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTopId, topTree]);

  const activeNode = useMemo(
    () => (Array.isArray(topTree) ? topTree : []).find((n) => n.ID === activeTopId) || null,
    [topTree, activeTopId]
  );

  const withLangPrefix = (path: string) => {
    // Default language has no URL prefix. If a backend URL happens to include
    // the default-lang prefix, strip it.
    if (isDefaultLang) {
      const prefix = `/${activeLang}`;
      if (path === prefix) return "/";
      if (path.startsWith(prefix + "/")) return path.slice(prefix.length);
      return path;
    }

    const clean = path === "/" ? "" : path;
    if (clean === `/${activeLang}` || clean.startsWith(`/${activeLang}/`)) return clean || `/${activeLang}`;
    return `/${activeLang}${clean}`;
  };

  const resolveHref = (rawUrl: string) => {
    if (isExternal(rawUrl)) return rawUrl;
    const path = rawUrl.startsWith("/") ? rawUrl : `/${rawUrl}`;
    return withLangPrefix(path);
  };

  return (
    <div className="relative lg:sticky lg:top-0 z-[99] shadow-flat">
      {/* MOBILE BAR */}
      <div className="lg:hidden py-3 px-4">
        <div className="container">
          <div className="grid items-center grid-cols-[auto_1fr_auto] gap-x-3">
            <button
              type="button"
              className="inline-flex items-center justify-center w-8 h-8"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
              onClick={() => {
                setMobileOpen((v) => !v);
                setActiveTopId(null);
              }}
            >
              <span className="sr-only">Menu</span>
              <HamburgerIcon open={mobileOpen} />
            </button>

            <div className="flex justify-center">
              <Link href={homeHref} aria-label={siteName} className="inline-flex">
                <span className="sr-only">{siteName}</span>
                <span className="relative block w-[132px] h-[42px]">
                  {logoSrc ? (
                    <Image
                      src={logoSrc}
                      alt={siteName}
                      fill
                      className="object-contain"
                      sizes="132px"
                      priority
                    />
                  ) : null}
                </span>
              </Link>
            </div>

            <div className="flex items-center justify-end gap-6">
              {showAccountIcon ? <AccountIconLinkClient /> : null}
              <CartBadgeClient />
            </div>
          </div>
        </div>
      </div>

      {/* DESKTOP BAR: lg+ */}
      <div
        className="hidden lg:block bg-fuji-grey relative"
        onMouseLeave={() => {
          setActiveTopId(null);
          showIndicatorForId(null);
        }}
      >
        <nav className="px-4">
          <div className="container">
            <ul ref={navRef} className="list-none m-0 flex items-center justify-between gap-x-6 h-[52px] relative">
              <li
                aria-hidden="true"
                className="absolute bottom-[-2px] h-[2px] bg-fuji-red transition-all duration-200 ease-out"
                style={{
                  transform: `translateX(${indicator.left}px)`,
                  width: `${indicator.width}px`,
                  opacity: indicator.visible ? 1 : 0,
                  transition: indicator.noTransition ? "none" : undefined,
                }}
              />

              {(Array.isArray(topTree) ? topTree : []).map((node) => {
                const id = Number(node.ID);
                const rawUrl = String(node.url ?? "");
                const placeholder = isPlaceholder(rawUrl);
                const hasChildren = (node.children?.length ?? 0) > 0;

                const isActive = activeTopId === id;

                const img = imageById[String(id)];
                const hasImg = !!img?.url;

                const content = hasImg ? (
                  <MenuItemImageBox img={img} fallbackAlt={node.title || "Menu item"} />
                ) : (
                  <MenuLabel html={String(node.title ?? "")} active={isActive} />
                );

                const hoverProps = {
                  onMouseEnter: () => {
                    setActiveTopId(id);
                  },
                };

                if (placeholder) {
                  return (
                    <li
                      key={id}
                      ref={(el) => {
                        itemRefs.current[id] = el;
                      }}
                      className="mb-0 flex-none h-full flex items-center"
                      {...hoverProps}
                    >
                      <span
                        className={[
                          "h-full flex items-center select-none",
                          hasChildren ? "cursor-pointer" : "",
                          hasImg ? "" : "group",
                        ].join(" ")}
                      >
                        {content}
                      </span>
                    </li>
                  );
                }

                const href = resolveHref(rawUrl);

                return (
                  <li
                    key={id}
                    ref={(el) => {
                      itemRefs.current[id] = el;
                    }}
                    className="mb-0 flex-none h-full flex items-center"
                    {...hoverProps}
                  >
                    <Link
                      href={href}
                      target={node.target ?? "_self"}
                      className={[
                        "h-full flex items-center",
                        "no-underline",
                        hasImg ? "hover:opacity-90" : "group",
                      ].join(" ")}
                    >
                      {content}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>

        {activeNode && (activeNode.children?.length || 0) > 0 && (
          <div
            role="region"
            aria-label="Mega menu"
            className="absolute top-full left-0 right-0 z-50 border-t border-fuji-grey-dark/40 bg-white shadow-flat"
            onMouseEnter={() => {
              setActiveTopId(activeNode.ID);
            }}
          >
            <div className="px-4">
              <div className="container py-6">
                <div className="grid grid-cols-12 gap-x-6 items-start">
                  <div className="col-span-12 md:col-span-6 min-w-0">
                    <ul className="grid grid-flow-col auto-cols-[minmax(200px,1fr)] grid-rows-4 gap-y-2 gap-x-6 list-none m-0 p-0 max-w-full min-w-0">
                      {activeNode.children?.map((child) => (
                        <li key={child.ID}>
                          <Link
                            href={resolveHref(child.url)}
                            target={child.target ?? "_self"}
                            className="text-body no-underline hover:underline whitespace-nowrap truncate"
                            onClick={() => {
                              setActiveTopId(null);
                              showIndicatorForId(null);
                            }}
                          >
                            <Html html={String(child.title ?? "")} />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="col-span-12 md:col-span-6 min-w-0">
                    <div className="grid grid-cols-2 gap-x-[20px] justify-items-stretch items-start w-full">
                      {(() => {
                        const first = activeNode.mega?.image_first || null;
                        const second = activeNode.mega?.image_second || null;
                        const images = [first, second].filter(Boolean) as MegaImage[];

                        if (images.length === 1) {
                          const img = images[0];
                          return (
                            <>
                              <div />
                              <MegaImageBox image={img} href={img.linkUrl ? resolveHref(img.linkUrl) : undefined} />
                            </>
                          );
                        }

                        return (
                          <>
                            {first && (
                              <MegaImageBox
                                image={first}
                                href={first.linkUrl ? resolveHref(first.linkUrl) : undefined}
                              />
                            )}
                            {second && (
                              <MegaImageBox
                                image={second}
                                href={second.linkUrl ? resolveHref(second.linkUrl) : undefined}
                              />
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* MOBILE MENU */}
      {mobileOpen && (
        <div className="lg:hidden bg-white">
          <ul className="list-none m-0 p-0">
            {(Array.isArray(topTree) ? topTree : []).map((node, idx) => {
              const id = Number(node.ID);
              const rawUrl = String(node.url ?? "");
              const placeholder = isPlaceholder(rawUrl);
              const hasChildren = (node.children?.length ?? 0) > 0;
              const expanded = activeTopId === id;

              const img = imageById[String(id)];
              const hasImg = !!img?.url;

              const titleContent = hasImg ? (
                <MenuItemImageBox img={img} fallbackAlt={node.title || "Menu item"} />
              ) : (
                <span className="grid text-[16px] leading-6 font-medium">
                  <span className={`${expanded ? "font-bold" : ""} col-start-1 row-start-1`}>
                    <Html html={String(node.title ?? "")} />
                  </span>
                  <span aria-hidden="true" className="invisible font-bold col-start-1 row-start-1">
                    <Html html={String(node.title ?? "")} />
                  </span>
                </span>
              );

              const rowBase = "flex items-center justify-between gap-2 px-4 py-3";

              const isLeafLink = !placeholder && !hasChildren;

              return (
                <li key={id} className="mb-0">
                  {isLeafLink ? (
                    <Link
                      href={resolveHref(rawUrl)}
                      target={node.target ?? "_self"}
                      className={`${rowBase} w-full no-underline hover:underline`}
                      onClick={() => {
                        setMobileOpen(false);
                        setActiveTopId(null);
                      }}
                      aria-label={node.title || "Menu item"}
                    >
                      <div className="min-w-0">{titleContent}</div>

                      <span className="inline-flex items-center justify-center w-8 h-8 flex-none">
                        <ArrowIcon width={18} height={18} />
                      </span>
                    </Link>
                  ) : (
                    <div
                      role={!placeholder && hasChildren ? "button" : undefined}
                      tabIndex={!placeholder && hasChildren ? 0 : undefined}
                      aria-expanded={!placeholder && hasChildren ? expanded : undefined}
                      aria-controls={!placeholder && hasChildren ? `submenu-${id}` : undefined}
                      onClick={!placeholder && hasChildren ? () => setActiveTopId(expanded ? null : id) : undefined}
                      onKeyDown={
                        !placeholder && hasChildren
                          ? (e: React.KeyboardEvent) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setActiveTopId(expanded ? null : id);
                              }
                            }
                          : undefined
                      }
                      className={`${rowBase} ${!placeholder && hasChildren ? "cursor-pointer" : ""}`}
                    >
                      <div className="min-w-0">{titleContent}</div>

                      {hasChildren ? (
                        <button
                          type="button"
                          aria-label={expanded ? "Collapse" : "Expand"}
                          aria-expanded={expanded}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveTopId(expanded ? null : id);
                          }}
                          className="inline-flex items-center justify-center w-8 h-8 flex-none"
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            aria-hidden="true"
                          >
                            {expanded ? (
                              <>
                                <line x1="5" y1="5" x2="19" y2="19" />
                                <line x1="19" y1="5" x2="5" y2="19" />
                              </>
                            ) : (
                              <>
                                <line x1="12" y1="3" x2="12" y2="21" />
                                <line x1="3" y1="12" x2="21" y2="12" />
                              </>
                            )}
                          </svg>
                        </button>
                      ) : (
                        <span className="inline-flex items-center justify-center w-8 h-8 flex-none" />
                      )}
                    </div>
                  )}

                  {hasChildren && (
                    <div id={`submenu-${id}`} className={expanded ? "block" : "hidden"}>
                      <div className="pb-2">
                        <ul className="list-none m-0 p-0">
                          {node.children?.map((child) => (
                            <li key={child.ID}>
                              <Link
                                href={resolveHref(child.url)}
                                target={child.target ?? "_self"}
                                className="flex items-center justify-between px-4 py-2 no-underline hover:underline"
                                onClick={() => {
                                  setMobileOpen(false);
                                  setActiveTopId(null);
                                }}
                              >
                                <span className="min-w-0 truncate">
                                  <Html html={String(child.title ?? "")} />
                                </span>

                                <span className="inline-flex items-center justify-center w-8 h-8 flex-none">
                                  <ArrowIcon width={18} height={18} />
                                </span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {idx < topTree.length - 1 && <div className="border-b border-fuji-black mx-4" />}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}